import React, { useState, useRef, useEffect } from "react";
import { catalogData } from "../data/mock";
import { useAudioPlayer } from "../hooks/useAudioPlayer";
import { Download, Tv, TrendingUp, CheckCircle, ArrowLeft, Music, Play, Pause, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Link } from "react-router-dom";

// Audio Player Component for Catalog
const AudioPlayer = ({ track, isActive, onPlay, onPause, globalAudioRef }) => {
  const {
    audioRef,
    progressRef,
    currentTime,
    duration,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleProgressClick,
    handlePlayToggle,
    formatTime,
    progress,
    hasAudio
  } = useAudioPlayer(track.streamUrl, isActive, onPlay, onPause, globalAudioRef);

  return (
    <div className="flex items-center gap-3 flex-1">
      {hasAudio && (
        <audio
          ref={audioRef}
          src={track.streamUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={onPause}
          preload="metadata"
        />
      )}

      {/* Play Button */}
      <button
        onClick={handlePlayToggle}
        disabled={!hasAudio}
        data-testid={`play-${track.id}`}
        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
          !hasAudio 
            ? "bg-[rgb(40,42,45)] text-[rgb(80,80,90)] cursor-not-allowed"
            : isActive 
              ? "bg-[rgb(218,255,1)] text-[rgb(17,17,19)] shadow-[0_0_15px_rgba(218,255,1,0.4)]" 
              : "bg-[rgb(40,42,45)] text-[rgb(218,255,1)] hover:bg-[rgba(218,255,1,0.2)]"
        }`}
      >
        {isActive ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
      </button>

      {/* Progress Bar (only show when has audio) */}
      {hasAudio && (
        <div className="flex-1 min-w-0 hidden sm:block">
          <div 
            ref={progressRef}
            onClick={handleProgressClick}
            className="relative h-1.5 bg-[rgb(40,42,45)] rounded-full overflow-hidden cursor-pointer group"
          >
            <motion.div 
              className="absolute h-full bg-gradient-to-r from-[rgb(218,255,1)] to-[rgb(180,220,0)] rounded-full"
              style={{ width: `${progress}%` }}
              initial={false}
              transition={{ duration: 0.1 }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[rgb(100,100,110)] text-[9px] font-mono">{formatTime(currentTime)}</span>
            <span className="text-[rgb(100,100,110)] text-[9px] font-mono">{formatTime(duration)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Track Player Component with streaming audio
const TrackPlayer = ({ track, isActive, onPlay, onPause, globalAudioRef }) => {
  const hasAudio = track.streamUrl && track.streamUrl !== "";
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={`group flex items-center justify-between p-4 rounded-xl transition-all duration-300 ${
        isActive 
          ? "bg-[rgba(218,255,1,0.1)] border border-[rgba(218,255,1,0.3)]" 
          : "bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.06)] hover:border-[rgba(218,255,1,0.2)]"
      }`}
    >
      <div className="flex items-center gap-4 flex-1">
        {/* Audio Player */}
        <AudioPlayer 
          track={track}
          isActive={isActive}
          onPlay={onPlay}
          onPause={onPause}
          globalAudioRef={globalAudioRef}
        />

        {/* Track Info */}
        <div className="min-w-0">
          <h4 className="text-white text-sm font-semibold truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {track.title}
          </h4>
          <p className="text-[rgb(161,161,170)] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
            {track.genre ? `${track.genre} • ${track.bpm} BPM • ${track.key}` : `${track.mix} • ${track.bpm} BPM • ${track.key}`}
          </p>
          {track.description && (
            <p className="text-[rgb(100,100,110)] text-[10px] mt-1 line-clamp-1" style={{ fontFamily: "'Inter', sans-serif" }}>
              {track.description}
            </p>
          )}
          {!hasAudio && (
            <p className="text-amber-500/70 text-[10px] mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
              Available in download package
            </p>
          )}
        </div>
      </div>

      {/* Duration & Status */}
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="hidden md:flex flex-col items-end mr-2">
          {track.mood && (
            <span className="text-[rgb(161,161,170)] text-[10px] tracking-wide uppercase">
              {track.mood}
            </span>
          )}
          {track.production && (
            <span className="text-[rgb(218,255,1)] text-[9px] tracking-wide uppercase">
              {track.production}
            </span>
          )}
        </div>
        <span className="text-[rgb(100,100,110)] text-xs font-mono hidden sm:block">
          {track.duration}
        </span>
        <div className="flex items-center gap-1.5 text-[rgb(74,222,128)]">
          <CheckCircle size={12} />
          <span className="text-[10px] tracking-wide hidden sm:block" style={{ fontFamily: "'Inter', sans-serif" }}>
            CLEARED
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// Category Icon Mapping
const CategoryIcon = ({ icon, size = 24, className }) => {
  const icons = {
    Tv: Tv,
    Zap: Zap,
    TrendingUp: TrendingUp
  };
  const IconComponent = icons[icon] || Music;
  return <IconComponent size={size} className={className} />;
};

// Category Section Component
const CategorySection = ({ category, index, activeTrack, setActiveTrack, globalAudioRef }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + index * 0.1 }}
      className="mb-12"
    >
      {/* Category Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[rgba(218,255,1,0.1)] flex items-center justify-center flex-shrink-0">
          <CategoryIcon icon={category.icon} size={24} className="text-[rgb(218,255,1)]" />
        </div>
        <div>
          <h3 className="text-[rgb(218,255,1)] text-lg font-bold tracking-wide" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {category.title}
          </h3>
          <p className="text-white text-xs font-medium tracking-wide mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
            {category.subtitle}
          </p>
          <p className="text-[rgb(161,161,170)] text-xs max-w-lg mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            {category.vibe}
          </p>
          {category.producer && (
            <p className="text-[rgb(100,100,110)] text-[10px] font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
              Produced by: <span className="text-[rgb(218,255,1)]">{category.producer}</span>
            </p>
          )}
        </div>
      </div>

      {/* Tracks */}
      <div className="space-y-2 pl-0 sm:pl-16">
        {category.tracks.map((track) => (
          <TrackPlayer
            key={track.id}
            track={track}
            isActive={activeTrack === track.id}
            onPlay={() => setActiveTrack(track.id)}
            onPause={() => setActiveTrack(null)}
            globalAudioRef={globalAudioRef}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Component removed - Industrial Dread Teaser no longer needed

const CatalogPage = () => {
  const [activeTrack, setActiveTrack] = useState(null);
  const globalAudioRef = useRef(null);

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden" data-testid="catalog-page">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <ArrowLeft size={14} />
          BACK TO HOME
        </Link>

        {/* Suite Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <TrendingUp size={12} className="mr-2" />
            PREMIUM CATALOG
          </Badge>

          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
          >
            {catalogData.suiteName.split(' ').map((word, i) => 
              word === 'ITZGREEN' || word === 'MUSIC' 
                ? <span key={i} className="text-[rgb(218,255,1)]">{word} </span> 
                : word + ' '
            )}
          </h1>

          <p
            className="text-[rgb(161,161,170)] text-sm sm:text-base max-w-2xl leading-relaxed mb-6"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {catalogData.description}
          </p>

          {/* Credits */}
          <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 sm:p-5 inline-block">
            <div className="space-y-2 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
              <p className="text-[rgb(161,161,170)]">
                <span className="text-[rgb(100,100,110)]">100% Human-Produced:</span>{" "}
                <span className="text-white font-medium">Zero Generative AI</span>
              </p>
              <p className="text-[rgb(161,161,170)]">
                <span className="text-[rgb(100,100,110)]">One-Stop Cleared:</span>{" "}
                <span className="text-white font-medium">Ready for immediate licensing</span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Streaming Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 mb-12 flex items-center gap-3"
        >
          <Music size={18} className="text-[rgb(218,255,1)] flex-shrink-0" />
          <p className="text-[rgb(161,161,170)] text-xs sm:text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
            <span className="text-white font-medium">Looking to stream?</span>{" "}
            Access our full streaming catalog in the{" "}
            <Link to="/vault" className="text-[rgb(218,255,1)] font-medium hover:underline">
              Supervisor Vault →
            </Link>
          </p>
        </motion.div>

        <Separator className="bg-[rgb(63,63,63)] mb-12" />

        {/* Categories */}
        {catalogData.categories.map((category, index) => (
          <CategorySection
            key={category.id}
            category={category}
            index={index}
            activeTrack={activeTrack}
            setActiveTrack={setActiveTrack}
            globalAudioRef={globalAudioRef}
          />
        ))}

        <Separator className="bg-[rgb(63,63,63)] my-10" />

        {/* CTA Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <p className="text-[rgb(161,161,170)] text-sm mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            Ready to license? Get one-stop clearance in 24 hours.
          </p>
          <Link
            to="/licensing"
            data-testid="license-cta"
            className="inline-flex items-center gap-2 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-sm font-semibold tracking-[0.1em] px-8 py-4 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4)] transition-all duration-300"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            LICENSE THIS CATALOG
          </Link>
        </motion.div>
      </div>
    </main>
  );
};

export default CatalogPage;
